import type { ChartDataPointSelection } from "../../components/molecules/ChartWidget/ChartWidget.types";
import type { DropdownOption } from "../../components/molecules/Dropdown/Dropdown";
import type { HomeStoryListItem } from "../home/homeDataStories.types";
import { toFilterChipLabel } from "./crossFilterMappers";
import type {
  CrossFilterCondition,
  CrossFilterTrailStep,
  CrossFilterValue,
  StoryFilterCondition,
} from "./crossFilters.types";
import { toFieldKey } from "./storyFiltersMappers";
import type { FilterScalarApi, StoryFilterApi } from "./storyFilters.types";
import type {
  DrillThroughCarriedFilters,
  DrillThroughContext,
  DrillThroughStoryMode,
  DrillThroughTarget,
} from "./drillThrough.types";

/**
 * URL params that carry a drill-through into the target story. The context
 * lives in the URL (not router state) so a refresh or a shared link keeps the
 * chip and the back link. The label is derived on read, never stored.
 */
export const DRILL_THROUGH_PARAMS = {
  sourceStoryId: "dt_source",
  field: "dt_field",
  value: "dt_value",
  /** JSON `DrillThroughCarriedFilters`; left out when the source had none. */
  filters: "dt_filters",
} as const;

/**
 * Chart id for carried Include/Exclude steps. It matches no chart in the
 * target story, so every chart narrows to them and none treats one as its
 * own clicked point.
 */
export const DRILL_THROUGH_CARRIED_CHART_ID = "drill-through";

/** `sales_region` / `salesRegion` → `Sales region`. */
export const humanizeField = (field: string): string => {
  const words = field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Chip copy, e.g. `Region in West`. */
export const toDrillThroughLabel = (field: string, value: CrossFilterValue): string =>
  toFilterChipLabel({
    fieldLabel: humanizeField(field),
    negated: false,
    valueLabels: [value === null ? "Blank" : String(value)],
  }).label;

/**
 * The clicked point as drill-through context, or null when the chart could
 * not resolve a dimension for it (nothing meaningful to carry across).
 */
export const toDrillThroughContext = (
  sourceStoryId: string,
  selection: Pick<ChartDataPointSelection, "field" | "value">,
): DrillThroughContext | null => {
  const { field, value } = selection;
  if (!sourceStoryId || !field || value === undefined) return null;
  return { sourceStoryId, field, value, label: toDrillThroughLabel(field, value) };
};

const isScalar = (value: unknown): value is CrossFilterValue =>
  value === null || ["string", "number", "boolean"].includes(typeof value);

/**
 * What travels from the source story: its top-bar filters and Include/Exclude
 * steps, without ids (the target makes its own). Drill-down steps stay
 * behind. Undefined when there is nothing to carry, which keeps the URL short.
 */
export const toDrillThroughCarriedFilters = (
  globalFilters: StoryFilterCondition[],
  steps: CrossFilterTrailStep[],
): DrillThroughCarriedFilters | undefined => {
  const carried: DrillThroughCarriedFilters = {
    globalFilters: globalFilters.map(({ columnId, operatorId, values }) => ({
      columnId,
      operatorId,
      values,
    })),
    crossFilters: steps.flatMap((step) =>
      step.kind !== "drillDown" && step.field && step.operator && step.value !== undefined
        ? [{ field: step.field, operator: step.operator, value: step.value }]
        : [],
    ),
  };
  return carried.globalFilters.length > 0 || carried.crossFilters.length > 0 ? carried : undefined;
};

const NEGATED_COMPARATORS: ReadonlySet<StoryFilterApi["comparator"]> = new Set(["not_in", "neq"]);

const toCarriedValues = (value: StoryFilterApi["value"]): FilterScalarApi[] =>
  Array.isArray(value) ? value : [value];

/**
 * The same payload from the server's trail rows: `global` rows are the top-bar filters and
 * `cross` rows the Include/Exclude steps, both in trail order. Drill, chart-level and baseline
 * rows stay behind (they re-scope one chart). Values travel raw (`West`), as the server holds them.
 * An Include/Exclude holds one value; a range or date-relative global filter has no option ids
 * to carry, so it is left out.
 */
export const toServerCarriedFilters = (
  trail: StoryFilterApi[],
): DrillThroughCarriedFilters | undefined => {
  const carried: DrillThroughCarriedFilters = {
    globalFilters: trail.flatMap((row) =>
      row.kind === "global" && ["eq", "neq", "in", "not_in"].includes(row.comparator)
        ? [
            {
              columnId: toFieldKey(row.field),
              operatorId: NEGATED_COMPARATORS.has(row.comparator) ? "is_not" : "is",
              values: toCarriedValues(row.value).map(String),
            },
          ]
        : [],
    ),
    crossFilters: trail.flatMap((row) =>
      row.kind === "cross" && !Array.isArray(toCarriedValues(row.value)[0])
        ? [
            {
              field: row.field.model_field_id,
              operator: NEGATED_COMPARATORS.has(row.comparator) ? ("isNot" as const) : ("is" as const),
              value: toCarriedValues(row.value)[0] ?? null,
            },
          ]
        : [],
    ),
  };
  return carried.globalFilters.length > 0 || carried.crossFilters.length > 0 ? carried : undefined;
};

/** Source-side carried filters from both places they can live; undefined when there are none. */
export const mergeCarriedFilters = (
  ...parts: Array<DrillThroughCarriedFilters | undefined>
): DrillThroughCarriedFilters | undefined => {
  const merged: DrillThroughCarriedFilters = {
    globalFilters: parts.flatMap((part) => part?.globalFilters ?? []),
    crossFilters: parts.flatMap((part) => part?.crossFilters ?? []),
  };
  return merged.globalFilters.length > 0 || merged.crossFilters.length > 0 ? merged : undefined;
};

/** The value is JSON-encoded so `2024` stays a number and `true` a boolean on the way back. */
export const toDrillThroughSearchParams = (context: DrillThroughContext): URLSearchParams => {
  const params = new URLSearchParams({
    [DRILL_THROUGH_PARAMS.sourceStoryId]: context.sourceStoryId,
    [DRILL_THROUGH_PARAMS.field]: context.field,
    [DRILL_THROUGH_PARAMS.value]: JSON.stringify(context.value),
  });
  if (context.carriedFilters) {
    params.set(DRILL_THROUGH_PARAMS.filters, JSON.stringify(context.carriedFilters));
  }
  return params;
};

const isStoryFilterCondition = (entry: unknown): entry is StoryFilterCondition => {
  const filter = entry as Partial<StoryFilterCondition> | null;
  return (
    !!filter &&
    typeof filter.columnId === "string" &&
    (filter.operatorId === null || typeof filter.operatorId === "string") &&
    Array.isArray(filter.values) &&
    filter.values.every((value) => typeof value === "string")
  );
};

const isCarriedCrossFilter = (
  entry: unknown,
): entry is DrillThroughCarriedFilters["crossFilters"][number] => {
  const filter = entry as Partial<CrossFilterCondition> | null;
  return (
    !!filter &&
    typeof filter.field === "string" &&
    (filter.operator === "is" || filter.operator === "isNot") &&
    isScalar(filter.value)
  );
};

/**
 * Reads `dt_filters` back. Broken JSON drops the param (the drill-through
 * itself still opens); a malformed entry drops only that entry.
 */
const fromCarriedFiltersParam = (raw: string | null): DrillThroughCarriedFilters | undefined => {
  if (raw === null) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  const { globalFilters, crossFilters } = (parsed ?? {}) as Record<string, unknown>;
  const carried: DrillThroughCarriedFilters = {
    globalFilters: (Array.isArray(globalFilters) ? globalFilters : [])
      .filter(isStoryFilterCondition)
      .map(({ columnId, operatorId, values }) => ({ columnId, operatorId, values })),
    crossFilters: (Array.isArray(crossFilters) ? crossFilters : [])
      .filter(isCarriedCrossFilter)
      .map(({ field, operator, value }) => ({ field, operator, value })),
  };
  return carried.globalFilters.length > 0 || carried.crossFilters.length > 0 ? carried : undefined;
};

/** Reads the context back from the target story's URL; null when absent or tampered with. */
export const fromDrillThroughSearchParams = (
  params: URLSearchParams,
): DrillThroughContext | null => {
  const sourceStoryId = params.get(DRILL_THROUGH_PARAMS.sourceStoryId);
  const field = params.get(DRILL_THROUGH_PARAMS.field);
  const rawValue = params.get(DRILL_THROUGH_PARAMS.value);
  if (!sourceStoryId || !field || rawValue === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(rawValue);
  } catch {
    return null;
  }
  if (!isScalar(value)) return null;
  const carriedFilters = fromCarriedFiltersParam(params.get(DRILL_THROUGH_PARAMS.filters));
  return {
    sourceStoryId,
    field,
    value,
    label: toDrillThroughLabel(field, value),
    ...(carriedFilters && { carriedFilters }),
  };
};

/** Carried top-bar filters as the target's filter-bar state — one per column, as the bar allows. */
export const toCarriedStoryFilters = (
  carried: DrillThroughCarriedFilters | null | undefined,
): Array<StoryFilterCondition & { id: string }> => {
  const seenColumnIds = new Set<string>();
  return (carried?.globalFilters ?? []).flatMap((filter) => {
    if (seenColumnIds.has(filter.columnId)) return [];
    seenColumnIds.add(filter.columnId);
    return [{ id: filter.columnId, ...filter }];
  });
};

/** Carried Include/Exclude as the target's own trail conditions, labelled like a local click. */
export const toCarriedCrossFilterConditions = (
  carried: DrillThroughCarriedFilters | null | undefined,
): CrossFilterCondition[] =>
  (carried?.crossFilters ?? []).map((filter, index) => ({
    id: `drill-through-${index}`,
    chartId: DRILL_THROUGH_CARRIED_CHART_ID,
    ...filter,
    label: String(filter.value),
  }));

/** A story's route in the given mode — present opens on its Story tab (the canvas). */
export const toStoryModePath = (
  workspaceId: string,
  storyId: string,
  mode: DrillThroughStoryMode,
): string =>
  mode === "present"
    ? `/workspace/${workspaceId}/story/${storyId}/present?tab=story`
    : `/workspace/${workspaceId}/story/${storyId}/build`;

/** Target story route with the drill-through context appended. */
export const toDrillThroughPath = (
  workspaceId: string,
  target: DrillThroughTarget,
  mode: DrillThroughStoryMode,
): string => {
  const base = toStoryModePath(workspaceId, target.storyId, mode);
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${toDrillThroughSearchParams(target.context).toString()}`;
};

/** Picker rows: story name only, keyed by id (the backend already filtered and ordered them). */
export const toDrillThroughStoryOptions = (
  stories: Array<Pick<HomeStoryListItem, "id" | "title">>,
): DropdownOption<string>[] => stories.map((story) => ({ value: story.id, label: story.title }));

/** "Back to <source story>"; a neutral fallback while the source title loads or is unavailable. */
export const toDrillThroughBackLabel = (sourceTitle: string | null | undefined): string =>
  sourceTitle?.trim() ? `Back to ${sourceTitle.trim()}` : "Back to previous story";
